import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveTenantId } from '@/lib/auth/tenant-id';
import { simpleModeRedirectPath } from '@/lib/nav/tenant-view-nav';
import {
  DEFAULT_TENANT_VIEW_MODE,
  type TenantViewMode,
} from '@/types/view-mode';

const STORAGE_PREFIX = 'tenant_view_mode';

interface TenantViewModeContextValue {
  mode: TenantViewMode;
  setMode: (mode: TenantViewMode) => void;
  isSimple: boolean;
  isExpert: boolean;
}

const TenantViewModeContext = createContext<TenantViewModeContextValue | null>(null);

function storageKey(tenantId: string | null): string {
  return tenantId ? `${STORAGE_PREFIX}:${tenantId}` : STORAGE_PREFIX;
}

function readStoredMode(tenantId: string | null): TenantViewMode {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    return raw === 'expert' ? 'expert' : DEFAULT_TENANT_VIEW_MODE;
  } catch {
    return DEFAULT_TENANT_VIEW_MODE;
  }
}

function writeStoredMode(tenantId: string | null, mode: TenantViewMode): void {
  try {
    localStorage.setItem(storageKey(tenantId), mode);
  } catch {
    // ignore quota / private mode
  }
}

export function TenantViewModeProvider({ children }: { children: ReactNode }) {
  const tenantId = resolveTenantId();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setModeState] = useState<TenantViewMode>(() => readStoredMode(tenantId));
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint in Tailwind
    };
    checkMobile();
    const handleResize = () => checkMobile();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Read stored mode on tenantId change
  useEffect(() => {
    setModeState(readStoredMode(tenantId));
  }, [tenantId]);

  const setMode = useCallback(
    (next: TenantViewMode) => {
      setModeState(next);
      writeStoredMode(tenantId, next);
    },
    [tenantId],
  );

  // Calculate effective mode: simple on mobile if stored mode is expert, otherwise use stored mode
  const effectiveMode = mode === 'expert' && isMobile ? 'simple' : mode;

  useEffect(() => {
    if (effectiveMode !== 'simple') return;
    const redirect = simpleModeRedirectPath(location.pathname);
    if (redirect && redirect !== location.pathname) {
      navigate(redirect, { replace: true });
    }
  }, [location.pathname, effectiveMode, navigate]);

  const value = useMemo(
    () => ({
      mode: effectiveMode,
      setMode,
      isSimple: effectiveMode === 'simple',
      isExpert: effectiveMode === 'expert',
    }),
    [effectiveMode, setMode],
  );

  return (
    <TenantViewModeContext.Provider value={value}>{children}</TenantViewModeContext.Provider>
  );
}

export function useTenantViewMode(): TenantViewModeContextValue {
  const ctx = useContext(TenantViewModeContext);
  if (!ctx) {
    throw new Error('useTenantViewMode must be used within TenantViewModeProvider');
  }
  return ctx;
}
